import { useState, useRef, useEffect } from "react";

export default function ContactSection() {
  const [visible, setVisible] = useState(false);
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">(
    "idle"
  );
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get("company_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      return;
    }

    // Remove honeypot from payload before sending
    formData.delete("company_alt");

    try {
      const response = await fetch(
        "https://formspree.io/f/mvzjakdr",
        {
          method: "POST",
          body: formData,
          headers: {
            Accept: "application/json",
          },
        }
      );
      // Formspree sometimes redirects (302) on success; treat that as success too
      if (response.ok || response.redirected || response.status === 302) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <section
      id="contact"
      className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-50 overflow-hidden relative"
    >
      <div
        ref={ref}
        className={`w-full max-w-6xl mx-auto transition-all duration-700 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 text-sm font-semibold rounded-full mb-4">
            Get in Touch
          </span>
          <h2 className="font-bold text-3xl md:text-4xl text-foreground-950 mb-4">
            Contact Us
          </h2>
          <p className="text-foreground-500 text-base md:text-lg max-w-xl mx-auto">
            Have questions about the books, bulk orders, or school visits? We
            would love to hear from you.
          </p>
          <a
            href="https://cappybarry.gumroad.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 text-primary-600 font-semibold hover:text-primary-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-store-2-line" />
            Visit our Gumroad Store
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        <div className="flex items-end justify-center">
          <img
            src="https://storage.readdy-site.link/project_files/c76c301d-9bec-452e-bd6e-c8e234254877/7a64b0d0-404d-4320-b209-8b67bd993511_compressed_2.webp"
            alt="Cappy bookend"
            className="hidden lg:block w-64 xl:w-80 h-auto object-contain flex-shrink-0 -mr-4 xl:-mr-6 z-10 relative"
          />

          <div className="bg-white rounded-2xl p-6 md:p-10 border border-background-200 shadow-sm w-full max-w-3xl">
            {formStatus === "success" && (
              <div className="mb-6 p-4 bg-primary-100 rounded-xl text-primary-800 text-center">
                <i className="ri-checkbox-circle-line text-2xl mb-2" />
                <p className="font-semibold">Thank you for your message!</p>
                <p className="text-sm">We will get back to you soon.</p>
              </div>
            )}
            {formStatus === "error" && (
              <div className="mb-6 p-4 bg-accent-100 rounded-xl text-accent-800 text-center">
                <i className="ri-error-warning-line text-2xl mb-2" />
                <p className="font-semibold">Something went wrong.</p>
                <p className="text-sm">Please try again later.</p>
              </div>
            )}

            <form
              id="contact-form"
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-foreground-800 mb-1.5"
                  >
                    Your Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-foreground-800 mb-1.5"
                  >
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                    placeholder="jane@example.com"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  Subject
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                  placeholder="Book inquiry"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
                  placeholder="Tell us what you need..."
                />
                <p className="text-xs text-foreground-400 mt-1 text-right">
                  Max 500 characters
                </p>
              </div>

              {/* Honeypot */}
              <div className="honeypot-field">
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-primary-500 text-white px-6 py-3.5 rounded-lg font-semibold text-base hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Send Message
                <i className="ri-send-plane-line ml-2" />
              </button>
            </form>
          </div>

          <img
            src="https://storage.readdy-site.link/project_files/c76c301d-9bec-452e-bd6e-c8e234254877/c0f1c07c-be64-434f-8a53-52e4ba8956ef_compressed_1.webp"
            alt="Barry bookend"
            className="hidden lg:block w-64 xl:w-80 h-auto object-contain flex-shrink-0 -ml-4 xl:-ml-6 z-10 relative"
          />
        </div>
      </div>
    </section>
  );
}